import { useState } from 'react';

// Pure view, matching the real SubsectionBuilderBlock's actual layout: one
// PTS-projekti dropdown, then every category as its own box shown at once
// (not a step-by-step wizard), each with one-click suggestion chips and a
// custom-name field. All data/handlers come from useKuntoarviovalikkoV2.
// No category icons — a tinted background already carries "has content",
// an icon added nothing.
const quietLinkStyle = { fontSize: '0.78rem', color: '#6c757d', fontWeight: 500, textDecoration: 'none', whiteSpace: 'nowrap' };
// The real navigation action (opens the standalone taloyhtiökortti table
// view) gets actual button weight — DB-taulut is a dev/debug tool and
// stays a quiet text link, but this one is a thing users actually want
// to find, so plain grey text undersold it.
const taulukkoBtnStyle = { fontSize: '0.78rem', color: '#fff', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', background: '#198754', border: '1px solid #198754', borderRadius: 6, padding: '5px 12px' };

// taulukkoUrl/editMode are passed down from KuntoarviovalikkoBlockV2 (the
// report-embedded caller) so this stays a pure view — it doesn't need to
// know about ReportTemplateContext itself. Both default to not-shown so
// other callers (e.g. a future standalone reuse) aren't forced to pass them.
export default function KuntoarviovalikkoView({ id_kiinteisto, svc, taulukkoUrl = null, editMode = false }) {
  const {
    ptsProjects, ptsLoaded, ptsProjectId, pickPtsProject,
    kategoriat, tkByCategory, suggestionsByCategory, error, status, savingKey,
    addTarkastuskohde, missingSections, creatingMissing, createMissingSections, deleteTarkastuskohde,
  } = svc;

  const [customInputs, setCustomInputs] = useState({});
  // Dismiss hides the banner for this page view only (not persisted) — it
  // reappears on next load if still unresolved, same as v1's own
  // session-only banners elsewhere in the report builder.
  const [missingBannerDismissed, setMissingBannerDismissed] = useState(false);

  const removeTk = (tk) => {
    if (window.confirm(`Poistetaanko tarkastuskohde "${tk.nimi}"? Tätä ei voi perua.`)) deleteTarkastuskohde(tk);
  };

  if (!id_kiinteisto) {
    return <div style={{ color: '#842029', background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, padding: '8px 12px', fontSize: '0.82rem' }}>
      Kiinteistön ID ei saatavilla tälle lohkolle.
    </div>;
  }

  return (
    <div style={{ fontSize: '0.875rem' }}>
      {error && (
        <div style={{ fontSize: '0.78rem', color: '#842029', background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, padding: '6px 10px', marginBottom: 10, whiteSpace: 'pre-wrap', userSelect: 'text' }}>{error}</div>
      )}
      {!error && status && (
        <div style={{ fontSize: '0.78rem', color: '#0f5132', background: '#d1e7dd', border: '1px solid #badbcc', borderRadius: 4, padding: '6px 10px', marginBottom: 10, whiteSpace: 'pre-wrap', userSelect: 'text' }}>{status}</div>
      )}

      {ptsLoaded && ptsProjects.length === 0 ? (
        <div className="mb-3 p-2 rounded border border-warning bg-warning bg-opacity-10">
          <div className="text-muted small">
            <i className="bi bi-exclamation-triangle me-1 text-warning" />
            Ei vielä PTS-projekteja — luo yksi taloyhtiökortin Kuntoarvio-välilehdeltä.
          </div>
        </div>
      ) : (
        <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
          <label className="text-muted small mb-0" style={{ whiteSpace: 'nowrap' }}>Linkitetty PTS-projekti:</label>
          <select className="form-select form-select-sm" style={{ maxWidth: 400, minWidth: 180 }}
            value={ptsProjectId ?? ''} onChange={e => pickPtsProject(e.target.value)}>
            <option value="">— Ei valittu —</option>
            {ptsProjects.map(p => <option key={p.id} value={p.id}>{p.name || p.nimi || `Projekti ${p.id}`}</option>)}
          </select>
          <span style={{ flex: 1 }} />
          {taulukkoUrl && (
            <a href={taulukkoUrl} target="_blank" rel="noreferrer" style={taulukkoBtnStyle}>Avaa taulukkonäkymässä</a>
          )}
          {editMode && (
            <a href={ptsProjectId ? `/db-viewer/${ptsProjectId}` : undefined} target="_blank" rel="noreferrer"
              onClick={e => { if (!ptsProjectId) e.preventDefault(); }}
              title={ptsProjectId ? 'Avaa projektin DB-taulut uuteen välilehteen' : 'Valitse ensin PTS-projekti'}
              style={{ ...quietLinkStyle, opacity: ptsProjectId ? 1 : 0.5, cursor: ptsProjectId ? 'pointer' : 'not-allowed' }}>
              DB-taulut
            </a>
          )}
        </div>
      )}

      {missingSections.length > 0 && !missingBannerDismissed && (
        <div className="mb-3 d-flex align-items-center gap-2 p-2 rounded" style={{ background: '#fff8e1', border: '1px solid #ffe082' }}>
          <i className="bi bi-exclamation-circle-fill" style={{ color: '#f9a825' }} />
          <div style={{ flex: 1, fontSize: '0.8rem', color: '#5d4600' }}>
            {missingSections.length} tarkastuskohde{missingSections.length > 1 ? 'tta' : ''} ({missingSections.map(m => m.tk.nimi).join(', ')}) on lisätty Kuntoarviovalikkoon, mutta {missingSections.length > 1 ? 'niillä' : 'sillä'} ei ole vielä osiota tässä raportissa.
          </div>
          <button type="button" className="btn btn-sm btn-success" disabled={creatingMissing} onClick={createMissingSections}>
            {creatingMissing ? 'Luodaan…' : 'Luo puuttuvat osiot'}
          </button>
          <button type="button" className="btn btn-sm btn-link text-muted" style={{ textDecoration: 'none' }} onClick={() => setMissingBannerDismissed(true)}>✕</button>
        </div>
      )}

      {ptsProjectId && kategoriat.map(cat => {
        const tarkastuskohteet = tkByCategory[cat.value] || [];
        const suggestions = suggestionsByCategory[cat.value] || [];
        const addedNames = new Set(tarkastuskohteet.map(tk => tk.nimi));
        const isActive = tarkastuskohteet.length > 0;
        const customVal = customInputs[cat.value] ?? '';

        return (
          <div key={cat.value} className="mb-2">
            <div className="d-flex align-items-center gap-2 px-2 py-2 rounded" style={{
              backgroundColor: isActive ? '#f0fdf4' : '#f8f9fa',
              border: `1px solid ${isActive ? '#86efac' : '#dee2e6'}`,
            }}>
              <span style={{ fontWeight: 600, color: isActive ? '#15803d' : '#343a40' }}>{cat.value}</span>
              {tarkastuskohteet.length > 0 && (
                <span className="badge rounded-pill bg-success ms-auto" style={{ fontSize: '0.6rem' }}>{tarkastuskohteet.length} osiota</span>
              )}
            </div>

            <div className="pt-2 pb-1 px-2" style={{ borderLeft: '2px solid #86efac', marginLeft: '0.5rem' }}>
              <div className="d-flex flex-wrap gap-1 mb-2">
                {/* Every real tarkastuskohde row renders on its own (never
                    deduped by name) so a duplicate — e.g. one accidentally
                    created by re-posting an already-used name — is visible
                    and individually deletable, instead of two DB rows
                    collapsing into one chip because they share a name. */}
                {tarkastuskohteet.map(tk => (
                  <span key={tk.id} className="btn btn-sm btn-success d-inline-flex align-items-center" style={{ fontSize: '0.75rem', padding: '0.15rem 0.3rem 0.15rem 0.6rem', gap: 4 }}>
                    <i className="bi bi-check" style={{ fontSize: '0.7rem' }} />{tk.nimi}
                    <button type="button" title="Poista tarkastuskohde" onClick={() => removeTk(tk)}
                      style={{ border: 'none', background: 'none', color: 'rgba(255,255,255,0.75)', padding: '0 2px', lineHeight: 1, fontSize: '0.85rem', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}>×</button>
                  </span>
                ))}
                {suggestions.filter(s => !addedNames.has(s)).map(s => {
                  const isSaving = savingKey === cat.value + ':' + s;
                  return (
                    <button key={s} type="button" disabled={isSaving}
                      onClick={() => addTarkastuskohde(cat.value, s)}
                      className="btn btn-sm btn-outline-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.15rem 0.6rem' }}>
                      {isSaving ? <span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} /> : s}
                    </button>
                  );
                })}
              </div>
              <div className="d-flex gap-2">
                <input type="text" className="form-control form-control-sm" placeholder="Uuden osion nimi…"
                  value={customVal} onChange={e => setCustomInputs(prev => ({ ...prev, [cat.value]: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && customVal.trim() && addTarkastuskohde(cat.value, customVal)} />
                <button className="btn btn-outline-success btn-sm text-nowrap" disabled={!customVal.trim()}
                  onClick={() => { addTarkastuskohde(cat.value, customVal); setCustomInputs(prev => ({ ...prev, [cat.value]: '' })); }}>
                  Luo osio
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
